import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, Injector, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfigStateService } from '@abp/ng.core';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { filter, map, pairwise } from 'rxjs/operators';
import { resolveUrl, API_PREFIX } from 'src/core/constants/api.constants';
import { PredefinedStateName } from 'src/core/constants/predefined-state-name.constant';
import {
  resolveStatusTone,
  getStatusColorClass,
  getStatusDotClass,
} from 'src/core/helpers/agent-status.helper';
import { resolveCurrentUserKey } from 'src/core/helpers/current-user-key.helper';
import { CallFacadeService } from 'src/app/shared/agent-desktop/telephony/services/call-facade.service';
import { CallStatus } from 'src/app/shared/agent-desktop/telephony/models/call-status.enum';
import { AgentStatusDto } from 'src/core/models/agent-status/agent-status-dto.model';
import { ChangeStatusInput } from 'src/core/models/agent-status/change-status-input.model';
import { CampaignAttribution } from 'src/core/models/agent-status/campaign-attribution.model';
import { InitSessionResultDto } from 'src/core/models/agent-status/init-session-result-dto.model';
import { StateType } from 'src/core/models/state-definition/state-type.enum';
import { StateDefinitionCacheService } from 'src/core/services/state-definition/state-definition-cache.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { AgentHeartbeatService } from 'src/core/services/signalr/agent-heartbeat.service';
import { AgentStatusAccessService } from './agent-status-access.service';
import { SipCredentialStore } from './sip-credential.store';

const BASE = `${API_PREFIX}/agent-desktop`;
const CHANGE_STATUS_URL = resolveUrl(`${BASE}/change-status`);
const INIT_SESSION_URL = resolveUrl(`${BASE}/init-session`);
const CAN_FORCE_AVAILABLE_URL = resolveUrl(`${BASE}/can-force-available`);

interface StatusChangeOptions {
  allowAutoExit?: boolean;
  syncTelephony?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AgentStatusService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly configState = inject(ConfigStateService);
  private readonly http = inject(HttpClient);
  private readonly facade = inject(CallFacadeService);
  private readonly oAuthService = inject(OAuthService);
  private readonly stateCache = inject(StateDefinitionCacheService);
  private readonly access = inject(AgentStatusAccessService);
  private readonly injector = inject(Injector);
  private readonly sipCredentialStore = inject(SipCredentialStore);
  private readonly pendingStatusRequests = signal(0);
  private readonly statusChangedSource = new Subject<void>();
  private heartbeat: AgentHeartbeatService | null = null;
  private initInFlight: Promise<void> | null = null;
  private sessionGeneration = 0;

  readonly currentStateId = signal<number | null>(null);
  readonly currentStateName = signal<string>(PredefinedStateName.Offline);
  readonly currentStateType = signal<StateType>(StateType.Offline);
  readonly queuedNextStateId = signal<number | null>(null);
  readonly sessionId = signal<number | null>(null);
  readonly isInitialized = signal(false);
  readonly activeConversationSegmentId = signal<number | null>(null);
  /** Emits only when the visible agent status actually changes. */
  readonly statusChanged$ = this.statusChangedSource.asObservable();

  readonly isEnabled = this.access.isEnabled;
  readonly isAvailable = computed(() => this.currentStateName() === PredefinedStateName.Available);
  readonly isOnCall = computed(() => this.currentStateName() === PredefinedStateName.OnCall);
  readonly isAcw = computed(() => this.currentStateName() === PredefinedStateName.Acw);
  readonly isBcw = computed(() => this.currentStateName() === PredefinedStateName.Bcw);
  readonly canChangeStatus = computed(
    () => this.isEnabled() && !this.isOnCall() && !this.isAcw() && !this.isBcw(),
  );
  readonly showGoAvailablePanel = computed(
    () => this.isInitialized() && !this.isAvailable() && this.canChangeStatus(),
  );
  /** True when FreeSwitch confirms no active channel for the agent's current segment.
   *  Refreshed server-side after each transition into OnCall or ACW. */
  readonly canForceAvailable = signal<boolean>(false);
  readonly isStatusChangeInProgress = computed(() => this.pendingStatusRequests() > 0);
  readonly statusTone = computed(() =>
    resolveStatusTone(this.currentStateName(), this.currentStateType()),
  );
  readonly statusColorClass = computed(() => getStatusColorClass(this.statusTone()));
  readonly statusDotClass = computed(() => getStatusDotClass(this.statusTone()));

  constructor() {
    // Resets all state when permission is revoked
    effect(() => {
      if (!this.isEnabled()) {
        this.resetState();
      }
    });

    this.oAuthService.events
      .pipe(
        filter(event => event.type === 'logout' || event.type === 'session_terminated'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.resetState());

    this.configState
      .getDeep$('currentUser')
      .pipe(
        map(currentUser => resolveCurrentUserKey(currentUser)),
        pairwise(),
        filter(
          ([previousUserKey, currentUserKey]) =>
            this.isInitialized() && previousUserKey != null && previousUserKey !== currentUserKey,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.resetState());
  }

  initSession(): Promise<void> {
    if (this.isInitialized()) {
      return Promise.resolve();
    }

    if (this.initInFlight) {
      return this.initInFlight;
    }

    const attempt: Promise<void> = this.runInitSession().finally(() => {
      if (this.initInFlight === attempt) {
        this.initInFlight = null;
      }
    });

    this.initInFlight = attempt;

    return attempt;
  }

  private async runInitSession(): Promise<void> {
    const generation = this.sessionGeneration;

    try {
      const result = await firstValueFrom(this.postInitSession());

      if (generation !== this.sessionGeneration) {
        return;
      }

      this.applySessionResult(result);
      this.isInitialized.set(true);
      this.subscribeToHeartbeatStatusChanges();
      await this.getHeartbeat().connect(result.sessionId);

      await this.syncTelephonyAvailability(result.currentStateName ?? PredefinedStateName.Offline);
    } catch (err) {
      // Do not set isInitialized on error — allow retry on next attempt.
      console.error('[AgentStatusService] initSession failed', err);
    }
  }

  async changeStatus(stateId: number): Promise<void> {
    await this.executeStatusChange(stateId);
  }

  async requestStatusChange(stateId: number): Promise<void> {
    if (this.isOnCall() || this.isAcw()) {
      if (this.hasLiveCallEngagement()) {
        this.queuedNextStateId.set(stateId);
      } else {
        await this.executeStatusChange(stateId, { allowAutoExit: true, syncTelephony: true });
      }
    } else {
      await this.changeStatus(stateId);
    }
  }

  setQueuedNextState(stateId: number | null): void {
    this.queuedNextStateId.set(stateId);
  }

  /** Called by the conversation fetch callback once the active segment is known. */
  updateActiveSegmentId(segmentId: number | null): void {
    this.activeConversationSegmentId.set(segmentId);
    if (segmentId != null) {
      this.getHeartbeat().setActiveSegment(segmentId);
    }
  }

  async handleCallStarted(
    segmentId?: number | null,
    attribution?: CampaignAttribution | null,
  ): Promise<void> {
    this.activeConversationSegmentId.set(segmentId ?? null);
    await this.transitionToState(PredefinedStateName.OnCall, {}, segmentId, attribution);
  }

  async handleAcwStarted(attribution?: CampaignAttribution | null): Promise<void> {
    await this.transitionToState(
      PredefinedStateName.Acw,
      {
        allowAutoExit: true,
        syncTelephony: true,
      },
      this.activeConversationSegmentId(),
      attribution,
    );
  }

  /**
   * Completes ACW. The campaign attribution is repeated here (it is also sent on ACW entry) because a
   * Reschedule-policy wrap-up must be bound to the campaign contact on the completing call itself.
   */
  async handleAcwCompleted(
    wrapUpId?: number | null,
    attribution?: CampaignAttribution | null,
    rescheduleAtUtc?: string | null,
  ): Promise<void> {
    const segmentId = this.activeConversationSegmentId();
    const queuedId = this.queuedNextStateId();
    if (queuedId != null) {
      const queuedState = this.stateCache.getByIdSync(queuedId);
      if (queuedState) {
        await this.executeStatusChange(queuedId, {
          allowAutoExit: true,
          syncTelephony: true,
        }, segmentId, wrapUpId, attribution, rescheduleAtUtc);
        this.queuedNextStateId.set(null);
        return;
      }

      this.queuedNextStateId.set(null);
    }

    const availableState = this.stateCache.getByName(PredefinedStateName.Available);
    if (availableState) {
      await this.executeStatusChange(availableState.id, {
        allowAutoExit: true,
        syncTelephony: true,
      }, segmentId, wrapUpId, attribution, rescheduleAtUtc);
    }
  }

  async handleBcwStarted(): Promise<void> {
    await this.transitionToState(PredefinedStateName.Bcw, {
      allowAutoExit: true,
      syncTelephony: true,
    }, this.activeConversationSegmentId());
  }

  async handleBcwCompleted(reason: 'call-started' | 'abandon'): Promise<void> {
    if (reason === 'call-started') {
      return;
    }

    const segmentId = this.activeConversationSegmentId();
    const availableState = this.stateCache.getByName(PredefinedStateName.Available);
    if (availableState) {
      await this.executeStatusChange(availableState.id, {
        allowAutoExit: true,
        syncTelephony: true,
      }, segmentId);
    }
  }

  async goAvailable(): Promise<void> {
    const availableState = this.stateCache.getByName(PredefinedStateName.Available);
    if (availableState) {
      await this.changeStatus(availableState.id);
    }
  }

  private async transitionToState(
    stateName: string,
    options: StatusChangeOptions = {},
    segmentId?: number | null,
    attribution?: CampaignAttribution | null,
  ): Promise<void> {
    const state = this.stateCache.getByName(stateName);
    if (!state) {
      return;
    }

    await this.executeStatusChange(state.id, {
      allowAutoExit: true,
      syncTelephony: false,
      ...options,
    }, segmentId, null, attribution);
  }

  private async executeStatusChange(
    stateId: number,
    options: StatusChangeOptions = {},
    segmentId?: number | null,
    wrapUpId?: number | null,
    attribution?: CampaignAttribution | null,
    rescheduleAtUtc?: string | null,
  ): Promise<void> {
    const { allowAutoExit = false, syncTelephony = true } = options;

    // allowAutoExit bypasses canChangeStatus for system-driven transitions.
    if (!allowAutoExit && !this.canChangeStatus()) {
      return;
    }

    if (!this.isInitialized()) {
      await this.initSession();
    }

    if (this.currentStateId() === stateId) {
      return;
    }

    if (!this.stateCache.getByIdSync(stateId)) {
      return;
    }

    await this.trackStatusChange(async () => {
      const previousStateId = this.currentStateId();
      const result = await firstValueFrom(
        this.postChangeStatus(stateId, segmentId, wrapUpId, attribution, rescheduleAtUtc),
      );
      this.applyStatus(result);

      if (syncTelephony) {
        await this.syncTelephonyAvailability(result.currentStateName);
      }

      if (previousStateId !== result.currentStateId) {
        this.statusChangedSource.next();
      }
    });
  }

  private applyStatus(dto: AgentStatusDto): void {
    this.sessionId.set(dto.sessionId);
    this.currentStateId.set(dto.currentStateId);
    this.currentStateName.set(dto.currentStateName);
    this.currentStateType.set(dto.currentStateType);
    this.refreshCanForceAvailableIfNeeded();
  }

  private applySessionResult(result: InitSessionResultDto): void {
    this.sessionId.set(result.sessionId);
    this.currentStateId.set(result.currentStateId ?? null);
    this.currentStateName.set(result.currentStateName ?? PredefinedStateName.Offline);
    this.currentStateType.set(result.currentStateType ?? StateType.Offline);
    if (result.sipCredentials) {
      this.sipCredentialStore.set(result.sipCredentials);
    }

    this.refreshCanForceAvailableIfNeeded();
  }

  private postInitSession(): Observable<InitSessionResultDto> {
    return this.http.post<InitSessionResultDto>(INIT_SESSION_URL, {});
  }

  private postChangeStatus(
    stateId: number,
    conversationSegmentId?: number | null,
    wrapUpId?: number | null,
    attribution?: CampaignAttribution | null,
    rescheduleAtUtc?: string | null,
  ): Observable<AgentStatusDto> {
    const input: ChangeStatusInput = { stateId };

    if (conversationSegmentId != null) {
      input.conversationSegmentId = conversationSegmentId;
    }

    if (wrapUpId != null) {
      input.wrapUpId = wrapUpId;
    }

    if (attribution != null) {
      input.campaignId = attribution.campaignId;
      input.campaignContactId = attribution.campaignContactId;
    }

    if (rescheduleAtUtc != null) {
      input.rescheduleAtUtc = rescheduleAtUtc;
    }

    return this.http.post<AgentStatusDto>(CHANGE_STATUS_URL, input);
  }

  private async trackStatusChange<T>(work: () => Promise<T>): Promise<T> {
    this.pendingStatusRequests.update(count => count + 1);
    try {
      return await work();
    } finally {
      this.pendingStatusRequests.update(count => Math.max(0, count - 1));
    }
  }

  private async ensureSipConnected(): Promise<void> {
    if (this.facade.isTransportConnected()) {
      return;
    }

    const status = this.facade.callStatus();
    if (status === CallStatus.Idle || status === CallStatus.Failed) {
      await this.facade.connect();
    }
  }

  /** True when THIS client genuinely has a live call or is in ACW. */
  private hasLiveCallEngagement(): boolean {
    if (this.facade.isAfterCallWork() || this.facade.currentSession() !== null) {
      return true;
    }
    const status = this.facade.callStatus();
    return (
      status === CallStatus.Active ||
      status === CallStatus.Held ||
      status === CallStatus.HangingUp ||
      status === CallStatus.Reconnecting
    );
  }

  private async syncTelephonyAvailability(stateName: string): Promise<void> {
    if (stateName === PredefinedStateName.Available) {
      await this.ensureSipConnected();
      await this.facade.setAgentAvailability(true);
      return;
    }

    if (stateName === PredefinedStateName.OnCall) {
      return;
    }

    const keepRegistration =
      stateName === PredefinedStateName.Acw || stateName === PredefinedStateName.Bcw;
    await this.facade.setAgentAvailability(false, { keepRegistration });
  }

  private resetState(): void {
    this.sessionGeneration++;
    if (this.heartbeat) {
      void this.heartbeat.disconnect();
    }
  
    if (this.facade.isTransportConnected()) {
      void this.facade.disconnect();
    }
    this.pendingStatusRequests.set(0);
    this.currentStateId.set(null);
    this.currentStateName.set(PredefinedStateName.Offline);
    this.currentStateType.set(StateType.Offline);
    this.queuedNextStateId.set(null);
    this.sessionId.set(null);
    this.isInitialized.set(false);
    this.initInFlight = null;
    this.activeConversationSegmentId.set(null);
    this.canForceAvailable.set(false);
    this.sipCredentialStore.clear();
  }

  private getHeartbeat(): AgentHeartbeatService {
    if (!this.heartbeat) {
      this.heartbeat = this.injector.get(AgentHeartbeatService);
    }

    return this.heartbeat;
  }

  private subscribeToHeartbeatStatusChanges(): void {
    const heartbeat = this.getHeartbeat();

    heartbeat.agentStatusChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(statusId => this.applyRemoteStatusChange(statusId));

    heartbeat.hubReconnected$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.onHubReconnected());
  }

  private async onHubReconnected(): Promise<void> {
    try {
      const result = await firstValueFrom(this.postInitSession());
      const previousStateId = this.currentStateId();
      this.applySessionResult(result);

      if (previousStateId !== result.currentStateId) {
        await this.syncTelephonyAvailability(result.currentStateName ?? PredefinedStateName.Offline);
        this.statusChangedSource.next();
      }

      if (this.isOnCall()) {
        // Re-register active segment with new connection ID if still on call.
        const segmentId = this.activeConversationSegmentId();
        if (segmentId != null) {
          this.getHeartbeat().setActiveSegment(segmentId);
        }
      } else {
        // Call was cleaned up during disconnect — clear stale segment reference.
        this.activeConversationSegmentId.set(null);
      }
    } catch (err) {
      console.error('[AgentStatusService] onHubReconnected refresh failed', err);
    }
  }

  private async applyRemoteStatusChange(statusId: number): Promise<void> {
    if (statusId === this.currentStateId()) {
      return;
    }

    const state = this.stateCache.getByIdSync(statusId);
    if (!state) {
      return;
    }

    const previousStateId = this.currentStateId();
    this.currentStateId.set(state.id);
    this.currentStateName.set(state.name);
    this.currentStateType.set(state.typeId);
    this.refreshCanForceAvailableIfNeeded();

    if (previousStateId !== state.id) {
      await this.syncTelephonyAvailability(state.name);
      this.statusChangedSource.next();
    }
  }

  refreshCanForceAvailableIfNeeded(): void {
    if (this.isOnCall() || this.isAcw()) {
      void this.checkCanForceAvailable();
    } else {
      this.canForceAvailable.set(false);
    }
  }

  private async checkCanForceAvailable(): Promise<void> {
    try {
      const result = await firstValueFrom(
        this.http.get<boolean>(CAN_FORCE_AVAILABLE_URL),
      );
      this.canForceAvailable.set(result);
    } catch {
      this.canForceAvailable.set(false);
    }
  }
}
